/**
 * Device Info Extraction AI Service
 *
 * Service for extracting device information using Google's Gemini AI API
 */

import { DeviceInfo } from "../vision/types";
import {
  GoogleGenerativeAI,
  HarmCategory,
  HarmBlockThreshold,
} from "@google/generative-ai";
import { AIModel } from "./config";

// Define the prompt template for the AI model
const DEVICE_INFO_PROMPT = `You are a specialized AI for extracting device information from text. 
Your task is to analyze the provided text and extract structured information about computing devices.

The text may come from various sources:
1. Screenshots of device settings pages
2. System information screens (like Windows msinfo32, macOS System Information, etc.)
3. Device specifications
4. Diagnostic reports
5. An image of a physical device (like a printed label, barcode, or physical sticker)
6. Labels on the bottom or back of devices (with serial numbers, model numbers, regulatory info)
7. Peripheral devices like keyboards, mice, monitors, printers, etc.
8. Android device settings or "About phone" screens

For device labels and external scans (like the bottom of a laptop or back of a peripheral):
- Focus on extracting the serial number, model number, and manufacturer
- Look for patterns like "S/N:", "P/N:", "Model:", "REV", "CN-", etc.
- Dell serial numbers often start with "CN-" followed by alphanumeric characters
- Pay attention to barcodes and their associated text
- Note manufacturing dates if present
- Identify the device type based on context (keyboard, mouse, monitor, etc.)

For Android devices:
- Look for the "About phone" or "About device" section
- Extract the device name, model number, Android version, and security patch level
- Note the processor/chipset information (often Snapdragon, Exynos, MediaTek, etc.)
- Extract IMEI numbers if present (usually in "Status" section)
- Look for build number, kernel version, and baseband version
- Note battery health and capacity information if available
- Extract Google Play system update information
- Pay special attention to "Hardware version" and "Model number" fields
- Model numbers often follow patterns like "SM-G925P" or "SM-A207F/DS" for Samsung devices
- Hardware versions may appear as alphanumeric codes like "G925P02"

For iOS devices:
- Look for the "About" section in the Settings app
- Extract the device name (often in the format "User's iPhone")
- Extract iOS version (e.g., "18.1.1")
- Extract model name (e.g., "iPhone 13 Pro")
- Extract model number (e.g., "MLR73LL/A")
- Extract serial number
- Look for storage capacity and available storage
- Note Wi-Fi and Bluetooth MAC addresses
- Extract IMEI, ICCID, and MEID numbers if present
- Look for carrier information and SIM status
- Note modem firmware version
- Extract EID (eSIM identifier) if present

For iOS devices, also extract (if present):
- iosVersion: iOS version number
- modelName: Model name (e.g., "iPhone 13 Pro")
- modelNumber: Model number (e.g., "MLR73LL/A")
- wifiAddress: Wi-Fi MAC address
- bluetoothAddress: Bluetooth MAC address
- modemFirmware: Modem firmware version
- eid: eSIM identifier
- iccid: ICCID number
- meid: MEID number
- carrierLock: Carrier lock status
- networkCarrier: Network carrier information
- totalCapacity: Total storage capacity
- availableCapacity: Available storage space

For macOS devices:
- Look for the "About This Mac" section in System Settings/System Preferences
- Extract the device name (e.g., "MacBook Air", "MacBook Pro", "iMac")
- Note the chip type (e.g., "Apple M2", "Intel Core i7")
- Extract the macOS version (e.g., "macOS Sonoma", "Version 14.6.1")
- Look for memory information (e.g., "8 GB")
- Extract the serial number
- Note display information including size and resolution (e.g., "13.6-inch (2560 x 1664)")
- Extract storage information (e.g., "40.11 GB available of 245.11 GB")
- Look for the model identifier (e.g., "Mac14,2")
- Extract the model number (e.g., "MLY33LL/A")
- Note system firmware and OS loader versions
- Extract hardware UUID and provisioning UDID if present
- Look for activation lock status
- Note processor details including number of cores and their types

For macOS devices, also extract (if present):
- modelIdentifier: Model identifier (e.g., "Mac14,2", "MacBookPro16,1")
- systemVersion: System version
- macOSKernelVersion: Kernel version
- bootVolume: Boot volume
- computerName: Computer name
- userFullName: User's full name
- secureVirtualMemory: Secure virtual memory status
- systemIntegrityProtection: System Integrity Protection status
- appleChip: Apple chip model (e.g., "Apple M2")
- macOSVersion: macOS version (e.g., "Sonoma 14.6.1")
- displayType: Display type (e.g., "Built-in Liquid Retina Display")
- displaySize: Display size (e.g., "13.6-inch")
- displayResolution: Display resolution (e.g., "2560 x 1664")
- systemFirmwareVersion: System firmware version
- osLoaderVersion: OS loader version
- hardwareUUID: Hardware UUID
- provisioningUDID: Provisioning UDID
- activationLockStatus: Activation lock status
- totalCores: Total number of cores
- coreDetails: Core details (e.g., "8 (4 performance and 4 efficiency)")
- macintoshHD: Macintosh HD information
- availableStorage: Available storage
- totalStorage: Total storage

Extract the following information (if present):
- make: The manufacturer/brand of the device (e.g., Apple, Dell, HP, Lenovo, Microsoft, Samsung)
- model: The specific model name/number
- type: The type of device (e.g., Laptop, Desktop, Tablet, Phone, Server, Keyboard, Mouse, Monitor, Printer)
- osName: The full name of the operating system (e.g., "Windows 11", "macOS Sonoma", "iOS 18", "Android 14")
- osVersion: The numeric version of the operating system - MUST BE NUMBERS ONLY (e.g., "11", "14.6.1", "18.1.1", "14"). Do not return "Version" or other text, only the version numbers.
- serialNumber: The device serial number
- year: The manufacturing or release year
- processor: CPU/processor information
- ram: RAM/memory information
- storage: Storage capacity information
- displaySize: Display size (e.g., 13.3", 15.6")
- displayResolution: Display resolution (e.g., 1920x1080, 2560x1600)
- color: Device color
- name: Device name (hostname)
- chipset: Chipset information
- batteryLevel: Battery level percentage
- imei: IMEI number (for mobile devices)
- phoneNumber: Associated phone number (for mobile devices)
- startupDisk: Startup disk information (for macOS)
- availableStorage: Available storage space
- totalStorage: Total storage capacity
- lastBackup: Last backup date/time
- manufacturingDate: Manufacturing date of the device
- revision: Revision or version of the hardware
- partNumber: Part number of the device or component
- regulatoryInfo: Regulatory information (FCC ID, CE mark, etc.)

For Windows devices, also extract (if present):
- systemName: System name or hostname
- systemManufacturer: System manufacturer (e.g., Microsoft, VMware, Dell)
- systemModel: System model (e.g., Surface Pro, VMware7,1)
- systemType: System type (e.g., x64-based PC)
- biosVersion: BIOS version
- biosDate: BIOS date
- biosManufacturer: BIOS manufacturer
- embeddedControllerVersion: Embedded controller version
- bootMode: Boot mode (e.g., UEFI)
- secureBootState: Secure boot state (e.g., On, Off)
- baseboardManufacturer: Baseboard/motherboard manufacturer
- baseboardProduct: Baseboard/motherboard product name
- baseboardVersion: Baseboard/motherboard version
- platformRole: Platform role (e.g., Desktop, Mobile, Workstation)
- pcr7Configuration: PCR7 Configuration
- windowsDirectory: Windows directory path
- systemDirectory: System directory path
- bootDevice: Boot device path
- locale: System locale
- hardwareAbstractionLayer: Hardware Abstraction Layer version
- userName: User name
- timeZone: Time zone
- installedPhysicalMemory: Installed physical memory (RAM)
- totalPhysicalMemory: Total physical memory
- availablePhysicalMemory: Available physical memory
- virtualMemoryMaxSize: Virtual memory maximum size
- virtualMemoryAvailable: Available virtual memory
- virtualMemoryInUse: Virtual memory in use

For Android devices, also extract (if present):
- androidVersion: Android version number
- securityPatchLevel: Security patch level date
- kernelVersion: Kernel version
- buildNumber: Build number
- baseband: Baseband or radio version
- batteryHealth: Battery health status
- batteryCapacity: Battery capacity in mAh
- screenRefreshRate: Screen refresh rate (e.g., 60Hz, 90Hz, 120Hz)
- simStatus: SIM card status (e.g., "SIM 1: Active", "SIM 2: Not inserted")
- networkOperator: Mobile network operator name
- googlePlaySystemUpdate: Google Play system update version/date
- deviceId: Android device ID
- ipAddress: IP address
- macAddress: MAC address
- bluetoothAddress: Bluetooth MAC address
- hardwareVersion: Hardware version (e.g., G925P02)
- modelNumber: Model number (e.g., SM-G925P, SM-A207F/DS)

Respond with a JSON object containing the extracted information and confidence scores for each field. Use the following format:

{
  "deviceInfo": {
    "make": "Apple",
    "model": "MacBook Pro",
    "type": "Laptop",
    "osName": "macOS Sonoma",
    "osVersion": "14.3.1",
    "serialNumber": "C02ZW1ZBMD6M",
    "year": "2023",
    "processor": "Apple M2 Pro",
    "ram": "16GB",
    "storage": "512GB SSD"
  },
  "confidence": {
    "overall": 85,
    "make": 90,
    "model": 80,
    "osName": 95,
    "osVersion": 90,
    "serialNumber": 70
  }
}

Important guidelines:
1. Only include fields that you can extract from the text
2. If you're uncertain about a field, provide a lower confidence score
3. Confidence scores should be integers between 0-100
4. For fields where you have high confidence (>90), try to clean up and standardize the values
5. For Windows System Information screens, pay special attention to the key-value pairs in the table
6. For virtual machines, correctly identify both the VM platform (e.g., VMware) and the guest OS
7. If the text is from a VM, set the make to the virtualization platform (e.g., "VMware", "Parallels", "VirtualBox")
8. For device labels and external scans, prioritize extracting serial numbers, model numbers, and manufacturer information
9. For Dell devices, look for serial numbers that start with "CN-" followed by alphanumeric characters
10. For peripheral devices (keyboard, mouse, etc.), identify the device type based on context
11. For Android devices, pay special attention to the "About phone" or "About device" section`;

/**
 * Request parameters for AI device info extraction
 */
export interface AIDeviceInfoRequest {
  text: string;
  context?: {
    deviceType?: string;
    make?: string;
    currentResults?: Partial<DeviceInfo>;
  };
}

/**
 * Response from AI device info extraction
 */
export interface AIDeviceInfoResponse {
  deviceInfo: Partial<DeviceInfo>;
  confidence: {
    overall: number;
    osName?: number;
    osVersion?: number;
    serialNumber?: number;
    year?: number;
    model?: number;
    make?: number;
    processor?: number;
    ram?: number;
    storage?: number;
    displaySize?: number;
    displayResolution?: number;
    color?: number;
    name?: number;
    chipset?: number;
    batteryLevel?: number;
    imei?: number;
    phoneNumber?: number;
    startupDisk?: number;
    availableStorage?: number;
    totalStorage?: number;
    lastBackup?: number;

    // Windows-specific confidence scores
    systemName?: number;
    systemManufacturer?: number;
    systemModel?: number;
    systemType?: number;
    biosVersion?: number;
    biosDate?: number;
    biosManufacturer?: number;
    embeddedControllerVersion?: number;
    bootMode?: number;
    secureBootState?: number;
    baseboardManufacturer?: number;
    baseboardProduct?: number;
    baseboardVersion?: number;
    platformRole?: number;
    pcr7Configuration?: number;
    windowsDirectory?: number;
    systemDirectory?: number;
    bootDevice?: number;
    locale?: number;
    hardwareAbstractionLayer?: number;
    userName?: number;
    timeZone?: number;
    installedPhysicalMemory?: number;
    totalPhysicalMemory?: number;
    availablePhysicalMemory?: number;
    virtualMemoryMaxSize?: number;
    virtualMemoryAvailable?: number;
    virtualMemoryInUse?: number;
  };
}

/**
 * Service for extracting device information using Google Generative AI
 */
export class DeviceInfoExtractionService {
  private model: any;

  /**
   * Creates a new Device Info Extraction Service
   * @param apiKey Google API key
   * @param modelName AI model to use
   */
  constructor(apiKey: string, modelName: AIModel = AIModel.GEMINI) {
    // Initialize the Google Generative AI client with the API key
    const genAI = new GoogleGenerativeAI(apiKey);

    // Get the model
    this.model = genAI.getGenerativeModel({
      model: modelName,
      generationConfig: {
        temperature: 0.2,
        topP: 0.8,
        topK: 40,
        maxOutputTokens: 1024,
      },
      safetySettings: [
        {
          category: HarmCategory.HARM_CATEGORY_HARASSMENT,
          threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
        },
        {
          category: HarmCategory.HARM_CATEGORY_HATE_SPEECH,
          threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
        },
        {
          category: HarmCategory.HARM_CATEGORY_SEXUALLY_EXPLICIT,
          threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
        },
        {
          category: HarmCategory.HARM_CATEGORY_DANGEROUS_CONTENT,
          threshold: HarmBlockThreshold.BLOCK_MEDIUM_AND_ABOVE,
        },
      ],
    });
  }

  /**
   * Extracts device information from text using Google's Generative AI
   * @param text The text to analyze
   * @param options Optional context to help with extraction
   * @returns The extracted device information with confidence scores
   */
  async extractDeviceInfo(
    text: string,
    options?: {
      deviceType?: string;
      make?: string;
      currentResults?: Partial<DeviceInfo>;
    }
  ): Promise<
    | {
        deviceInfo: Partial<DeviceInfo>;
        confidence: {
          overall: number;
          osName?: number;
          osVersion?: number;
          serialNumber?: number;
          year?: number;
          model?: number;
          make?: number;
          processor?: number;
          ram?: number;
          storage?: number;
          displaySize?: number;
          displayResolution?: number;
          color?: number;
          name?: number;
          chipset?: number;
          batteryLevel?: number;
          imei?: number;
          phoneNumber?: number;
          startupDisk?: number;
          availableStorage?: number;
          totalStorage?: number;
          lastBackup?: number;
        };
      }
    | undefined
  > {
    try {
      // Create the prompt with the text to analyze
      const prompt = this.createPrompt(text, options);

      // Generate content using the model
      const result = await this.model.generateContent(prompt);
      const response = result.response;
      const responseText = response.text();

      // Parse the response
      return this.parseResponse(responseText);
    } catch (error) {
      console.error("Error extracting device info:", error);
      return undefined;
    }
  }

  /**
   * Creates a prompt for the AI model
   * @param text The text to analyze
   * @param options Optional context to help with extraction
   * @returns The prompt for the AI model
   */
  private createPrompt(
    text: string,
    options?: {
      deviceType?: string;
      make?: string;
      currentResults?: Partial<DeviceInfo>;
    }
  ): string {
    // Start with the standard prompt
    let prompt = DEVICE_INFO_PROMPT;

    // Add the text to analyze
    prompt += `\n\nText to analyze:\n${text}`;

    // Add any context information if available
    if (options) {
      prompt += "\n\nAdditional context:";

      if (options.deviceType) {
        prompt += `\nDevice type: ${options.deviceType}`;
      }

      if (options.make) {
        prompt += `\nManufacturer: ${options.make}`;
      }

      if (options.currentResults) {
        prompt += "\nPartial information already extracted:";
        Object.entries(options.currentResults).forEach(([key, value]) => {
          if (
            value !== undefined &&
            key !== "confidence" &&
            key !== "aiEnhanced"
          ) {
            prompt += `\n- ${key}: ${value}`;
          }
        });
      }
    }

    return prompt;
  }

  /**
   * Parses the response from the AI model
   * @param responseText The response text from the AI model
   * @returns The extracted device information with confidence scores
   */
  private parseResponse(responseText: string): {
    deviceInfo: Partial<DeviceInfo>;
    confidence: {
      overall: number;
      osName?: number;
      osVersion?: number;
      serialNumber?: number;
      year?: number;
      model?: number;
      make?: number;
      processor?: number;
      ram?: number;
      storage?: number;
      displaySize?: number;
      displayResolution?: number;
      color?: number;
      name?: number;
      chipset?: number;
      batteryLevel?: number;
      imei?: number;
      phoneNumber?: number;
      startupDisk?: number;
      availableStorage?: number;
      totalStorage?: number;
      lastBackup?: number;

      // Windows-specific confidence scores
      systemName?: number;
      systemManufacturer?: number;
      systemModel?: number;
      systemType?: number;
      biosVersion?: number;
      biosDate?: number;
      biosManufacturer?: number;
      embeddedControllerVersion?: number;
      bootMode?: number;
      secureBootState?: number;
      baseboardManufacturer?: number;
      baseboardProduct?: number;
      baseboardVersion?: number;
      platformRole?: number;
      pcr7Configuration?: number;
      windowsDirectory?: number;
      systemDirectory?: number;
      bootDevice?: number;
      locale?: number;
      hardwareAbstractionLayer?: number;
      userName?: number;
      timeZone?: number;
      installedPhysicalMemory?: number;
      totalPhysicalMemory?: number;
      availablePhysicalMemory?: number;
      virtualMemoryMaxSize?: number;
      virtualMemoryAvailable?: number;
      virtualMemoryInUse?: number;
    };
  } {
    try {
      // Find the JSON object in the response
      const jsonMatch =
        responseText.match(/```json\s*([\s\S]*?)\s*```/) ||
        responseText.match(/\{[\s\S]*\}/);

      if (!jsonMatch) {
        throw new Error("No JSON found in response");
      }

      // Parse the JSON
      const jsonStr = jsonMatch[1] || jsonMatch[0];
      const parsedResponse = JSON.parse(jsonStr);

      // Extract device info and confidence scores
      const deviceInfo = parsedResponse.deviceInfo || parsedResponse;
      const confidenceScores = parsedResponse.confidence || { overall: 50 };

      // Convert confidence scores from 0-100 to 0-1 if needed
      const normalizedConfidence: Record<string, number> = {};

      Object.entries(confidenceScores).forEach(([key, value]) => {
        // If confidence is > 1, assume it's on a 0-100 scale and normalize
        if (typeof value === "number" && value > 1) {
          normalizedConfidence[key] = Math.min(value / 100, 1);
        } else {
          normalizedConfidence[key] = value as number;
        }
      });

      // Set overall confidence to 0.5 if not provided
      if (!normalizedConfidence.overall) {
        normalizedConfidence.overall = 0.5;
      }

      // Convert confidence from 0-1 to 0-100 for the final output
      const outputConfidence: Record<string, number> = {};
      Object.entries(normalizedConfidence).forEach(([key, value]) => {
        outputConfidence[key] = Math.round(value * 100);
      });

      deviceInfo.aiEnhanced = true;
      deviceInfo.confidence = outputConfidence.overall;

      // Clean up osVersion if it's not a valid version number
      if (deviceInfo.osVersion) {
        // Check if osVersion contains only "version" or similar text
        if (/^version$/i.test(deviceInfo.osVersion.trim())) {
          deviceInfo.osVersion = undefined;
        }
        // Remove any non-numeric characters except for dots (for version numbers like 14.6.1)
        else if (deviceInfo.osVersion) {
          const versionMatch = deviceInfo.osVersion.match(/(\d+(?:\.\d+)*)/);
          if (versionMatch) {
            deviceInfo.osVersion = versionMatch[1];
          } else if (!/\d/.test(deviceInfo.osVersion)) {
            // If there are no numbers at all, clear it
            deviceInfo.osVersion = undefined;
          }
        }
      }

      // Generate osName if it doesn't exist but osType and osVersion do
      if (!deviceInfo.osName && deviceInfo.osType && deviceInfo.osVersion) {
        deviceInfo.osName = `${deviceInfo.osType} ${deviceInfo.osVersion}`;
      } else if (!deviceInfo.osName && deviceInfo.osType) {
        deviceInfo.osName = deviceInfo.osType;
      }
      
      // If we have osName but no osVersion, try to extract it
      if (deviceInfo.osName && !deviceInfo.osVersion) {
        const versionMatch = deviceInfo.osName.match(/\b(\d+(?:\.\d+)*)\b/);
        if (versionMatch) {
          deviceInfo.osVersion = versionMatch[1];
        }
      }

      return {
        deviceInfo,
        confidence: outputConfidence as any,
      };
    } catch (error) {
      console.error("Error parsing AI response:", error);
      return {
        deviceInfo: {
          confidence: 0,
          error: `Failed to parse AI response: ${error}`,
        },
        confidence: {
          overall: 0,
        },
      };
    }
  }
}
